import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Sparkles, Upload, Check } from "lucide-react";
import Papa from "papaparse";

import { useAI } from "@/hooks/useAI";
import { useCleaningPreferences } from "@/hooks/useCleaningPreferences";
import { useActiveDataset } from "@/contexts/ActiveDatasetContext";
import { cleanRows, analyseRows, type CleaningIssue, type Row } from "@/lib/cleaning-engine";
import {
  parseUserFile,
  ACCEPTED_FILE_TYPES,
  ACCEPTED_HELP_TEXT,
} from "@/lib/file-parsing";

interface DataCleaningToolsProps {
  onCleaningComplete?: () => void;
}

/**
 * Cleaning is deterministic (src/lib/cleaning-engine.ts) and driven by the
 * user's saved preferences. Gemini adds an explanation of what it found, but
 * never invents rows or counts — every number below is measured.
 */
export const DataCleaningTools = ({ onCleaningComplete }: DataCleaningToolsProps) => {
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pastedData, setPastedData] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [issues, setIssues] = useState<CleaningIssue[] | null>(null);
  const [summary, setSummary] = useState<
    { actions: { action: string; rows_affected: number }[]; before: number; after: number } | null
  >(null);
  const [aiNotes, setAiNotes] = useState<string>("");

  const { preferences } = useCleaningPreferences();
  const { dataset, setDataset, updateRows } = useActiveDataset();
  const { call: callAI, isLoading: aiLoading } = useAI();

  const loadRows = (label: string, rows: Row[]) => {
    if (!rows.length) {
      toast.error("That file has no rows we can read.");
      return;
    }
    setDataset(label, rows);
    setIssues(analyseRows(rows, preferences));
    setSummary(null);
    setAiNotes("");
    toast.success(`Loaded ${rows.length} rows from ${label}`);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const parsed = await parseUserFile(file);
      if (parsed.kind !== "table" || !parsed.rows?.length) {
        toast.error("This file has no table in it. Upload a CSV or Excel file to clean.");
        return;
      }
      loadRows(file.name, parsed.rows);
    } catch (e: any) {
      toast.error(e?.message || "Could not read that file");
    }
  };

  const handleUsePasted = () => {
    if (!pastedData.trim()) return;
    const parsed = Papa.parse<Row>(pastedData.trim(), {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
    });
    if (parsed.errors.length && !parsed.data.length) {
      toast.error("That doesn't look like CSV data with a header row.");
      return;
    }
    loadRows("Pasted data", parsed.data as Row[]);
  };

  const handleClean = async () => {
    if (!dataset?.rows.length) {
      toast.error("Load a file or paste some data first.");
      return;
    }
    setIsProcessing(true);
    setProgress(15);
    try {
      const result = cleanRows(dataset.rows, preferences);
      setProgress(60);

      updateRows(result.rows, result.actions);
      setIssues(result.issues);
      setSummary({
        actions: result.actions,
        before: result.originalCount,
        after: result.cleanedCount,
      });

      if (result.actions.length === 0) {
        toast.success("Your data is already clean — nothing needed changing.");
      } else {
        toast.success(
          `Cleaned ${result.originalCount} rows → ${result.cleanedCount} rows`,
        );
      }
      setProgress(80);

      // AI explanation of the measured result (optional, never blocks).
      const ai = await callAI(
        {
          feature: "data_cleaning_intelligent",
          input:
            "Explain in plain language what these cleaning results mean for the business and what to watch out for. Do not invent numbers.",
          dataset: {
            issues: result.issues,
            actions: result.actions,
            sample: result.rows.slice(0, 30),
            row_count: result.cleanedCount,
          },
        },
        { silent: true },
      );
      if (ai.success) setAiNotes(ai.response || "");
      setProgress(100);
      onCleaningComplete?.();
    } catch (e: any) {
      toast.error(e?.message || "Cleaning failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const severityVariant = (s: CleaningIssue["severity"]) =>
    s === "high" ? "destructive" : s === "medium" ? "default" : "secondary";

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Clean your data</h2>
          <p className="text-sm text-muted-foreground">
            Uses your saved cleaning settings above. Every change is counted and shown.
          </p>
        </div>
        <Sparkles className="h-5 w-5 text-primary" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Upload a file</label>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-3">
            <input
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileUpload}
              className="hidden"
              id="cleaning-file-upload"
            />
            <label
              htmlFor="cleaning-file-upload"
              className="flex items-center gap-2 cursor-pointer"
            >
              <Upload className="h-4 w-4" />
              <span className="text-sm">{fileName || ACCEPTED_HELP_TEXT}</span>
            </label>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Or paste CSV rows</label>
          <Textarea
            placeholder="name,amount,date&#10;Ada,5000,2026-01-04"
            value={pastedData}
            onChange={(e) => setPastedData(e.target.value)}
            rows={3}
            className="resize-none"
          />
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={handleUsePasted}
            disabled={!pastedData.trim()}
          >
            Use this data
          </Button>
        </div>
      </div>

      {dataset && (
        <p className="text-sm text-muted-foreground">
          Working on <strong>{dataset.label}</strong> — {dataset.rows.length} rows,{" "}
          {dataset.columns.length} columns
        </p>
      )}

      {issues && issues.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            What we found ({issues.length})
          </h3>
          <ul className="space-y-1">
            {issues.slice(0, 12).map((issue, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span>{issue.description}</span>
                <Badge variant={severityVariant(issue.severity) as any}>
                  {issue.severity}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {issues && issues.length === 0 && dataset && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Check className="h-4 w-4 text-emerald-500" /> No problems found with your
          current settings.
        </p>
      )}

      {isProcessing && (
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-muted-foreground">{progress}% complete</p>
        </div>
      )}

      <Button
        onClick={handleClean}
        disabled={isProcessing || !dataset?.rows.length}
        className="w-full"
        size="lg"
      >
        {isProcessing ? (
          <>
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            Cleaning…
          </>
        ) : (
          "Clean my data"
        )}
      </Button>

      {summary && (
        <div className="rounded-lg border p-4 space-y-2">
          <h3 className="text-sm font-medium">
            Result: {summary.before} rows → {summary.after} rows
          </h3>
          {summary.actions.length > 0 ? (
            <ul className="text-sm text-muted-foreground list-disc ml-5">
              {summary.actions.map((a, i) => (
                <li key={i}>
                  {a.action} — {a.rows_affected} affected
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No changes were needed.</p>
          )}
          <p className="text-xs text-muted-foreground">
            Use "Save changes" below to keep this version.
          </p>
        </div>
      )}

      {(aiLoading || aiNotes) && (
        <div className="rounded-lg border bg-muted/40 p-4">
          <h3 className="text-sm font-medium mb-1">What this means</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {aiLoading && !aiNotes ? "Asking the AI to explain…" : aiNotes}
          </p>
        </div>
      )}
    </Card>
  );
};
